"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Input } from "reactstrap"
import DataTable from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import type { ExhibitionStandCategory } from "./types"
import { ExhibitionStandCategoryService } from "../../../Service/ExhibitionRelatedService"
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  exhibitionId: number
  onAdd: () => void
  onEdit: (item: ExhibitionStandCategory) => void
  refresh: boolean
}

const StandCategoriesTab: React.FC<Props> = ({ exhibitionId, onAdd, onEdit, refresh }) => {
  const [data, setData] = useState<ExhibitionStandCategory[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await ExhibitionStandCategoryService.getAll(exhibitionId)
      setData(response.results)
    } catch (err) {
      console.error("Failed to fetch exhibition stand categories:", err)
      toast.error("Failed to fetch exhibition stand categories")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [exhibitionId, refresh])

  const handleDelete = async (id: number, standName?: string) => {
    const result = await Swal.fire({
      title: "Delete Stand Category?",
      text: `This action will permanently remove "${standName || "this stand category"}" from the exhibition. This cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete It!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    })

    if (result.isConfirmed) {
      try {
        await ExhibitionStandCategoryService.delete(id)

        // Success alert
        await Swal.fire({
          title: "Deleted!",
          text: "Stand category has been successfully deleted.",
          icon: "success",
          confirmButtonColor: "#198754",
          timer: 2000,
          timerProgressBar: true,
        })

        fetchData()
      } catch (error) {
        // Error alert
        await Swal.fire({
          title: "Error!",
          text: "Failed to delete stand category. Please try again.",
          icon: "error",
          confirmButtonColor: "#dc3545",
        })
      }
    }
  }

  const filteredData = data.filter((item) => (item.stand_name || "").toLowerCase().includes(search.toLowerCase()))

  const columns = [
    {
      name: "Category ID",
      selector: (row: ExhibitionStandCategory) => row.category,
      sortable: true,
      width: "120px",
    },
    {
      name: "Category Name",
      selector: (row: ExhibitionStandCategory) => row.stand_name || "N/A",
      sortable: true,
    },
    
    {
      name: "Actions",
      cell: (row: ExhibitionStandCategory) => (
        <div className="d-flex gap-1">
          <Btn color="info" size="xs" onClick={() => onEdit(row)} title="Edit">
            <FaEdit size={12} />
          </Btn>
          <Btn color="danger" size="xs" onClick={() => handleDelete(row.id, row.stand_name)} title="Delete">
            <FaTrash size={12} />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "100px",
    },
  ]

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Input
          type="search"
          placeholder="Search exhibition stand categories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 300 }}
        />
        <Btn color="primary" size="sm" onClick={onAdd}>
          <FaPlus className="me-1" size={12} /> Add Exhibition Stand Category
        </Btn>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        progressPending={loading}
        pagination
        striped
        highlightOnHover
        responsive
        noDataComponent="No exhibition stand categories found."
      />
    </div>
  )
}

export default StandCategoriesTab
